import * as React from "react";
import { Check, ChevronsUpDown, X as XIcon, User, UserPlus, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * ClientCombobox — selettore cliente con autocomplete e ricerca live.
 * Props:
 *  - clients: array di clienti ({id, surname, name, email, mobile, phone, fiscal_code})
 *  - value: id cliente selezionato (string)
 *  - onChange: (clientId: string) => void
 *  - onClientCreated?: (client) => void — callback opzionale dopo creazione inline (per aggiornare la lista del parent)
 *  - placeholder: testo placeholder (default: "Seleziona un cliente…")
 *  - disabled: boolean
 *  - testid: data-testid del bottone (default: "client-combobox")
 *  - allowClear: mostra X per resettare (default: true)
 *  - allowCreate: mostra CTA "Nuovo cliente" (default: true)
 */
export function ClientCombobox({
  clients = [],
  value,
  onChange,
  onClientCreated,
  placeholder = "Seleziona un cliente…",
  disabled = false,
  testid = "client-combobox",
  allowClear = true,
  allowCreate = true,
}) {
  const [open, setOpen] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [newClient, setNewClient] = React.useState({
    surname: "", name: "", fiscal_code: "", mobile: "", email: "", city: "",
  });
  const sorted = React.useMemo(() => {
    return [...clients].sort((a, b) =>
      labelOf(a).toLowerCase().localeCompare(labelOf(b).toLowerCase(), "it"),
    );
  }, [clients]);
  const selected = React.useMemo(
    () => clients.find((c) => c.id === value),
    [clients, value],
  );
  // 🆕 Feb 2026 — Etichetta pulita: aziende → ragione sociale; persone → cognome nome (ignora surname '-')
  function labelOf(c) {
    if (!c) return "Cliente";
    if (c.subject_type === "company") {
      return (c.ragione_sociale || c.company_name || c.name || "").trim() || c.email || "Azienda";
    }
    const surname = (c.surname || "").trim();
    const name = (c.name || "").trim();
    const cleanSurname = surname === "-" ? "" : surname;
    const full = `${cleanSurname} ${name}`.replace(/\s+/g, " ").trim();
    return full || c.email || "Cliente";
  }

  const submitNewClient = async () => {
    if (!newClient.surname?.trim() || !newClient.name?.trim()) {
      return toast.error("Cognome e Nome sono obbligatori");
    }
    setCreating(true);
    try {
      const payload = {
        surname: newClient.surname.trim(),
        name: newClient.name.trim(),
        fiscal_code: newClient.fiscal_code?.trim() || null,
        mobile: newClient.mobile?.trim() || null,
        email: newClient.email?.trim() || null,
        city: newClient.city?.trim() || null,
      };
      // Rimuovi null per non inviare validator errati
      Object.keys(payload).forEach((k) => payload[k] == null && delete payload[k]);
      const { data } = await api.post("/clients", payload);
      toast.success(`Cliente ${data.surname} ${data.name} creato`);
      // Segnala al parent che deve aggiornare la lista e selezionare il nuovo cliente
      onClientCreated?.(data);
      onChange?.(data.id);
      setCreateOpen(false);
      setOpen(false);
      setNewClient({ surname: "", name: "", fiscal_code: "", mobile: "", email: "", city: "" });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal",
            !selected && "text-slate-400",
          )}
        >
          <span className="inline-flex items-center gap-2 truncate">
            <User className="h-4 w-4 text-slate-400 shrink-0" />
            <span className="truncate">{selected ? labelOf(selected) : placeholder}</span>
          </span>
          <span className="flex items-center gap-1">
            {allowClear && selected && (
              <span
                role="button"
                tabIndex={-1}
                data-testid={`${testid}-clear`}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange?.("");
                }}
                className="h-5 w-5 inline-flex items-center justify-center rounded hover:bg-slate-200 text-slate-500"
                title="Rimuovi selezione"
              >
                <XIcon className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(28rem,92vw)] p-0"
        align="start"
        // 🆕 Fix scroll dentro Dialog (Radix): evita che il modal padre catturi la ruota
        onWheel={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Command
          filter={(itemValue, search) => {
            // itemValue contiene "cognome nome email mobile phone fiscal_code" già lower-case
            return itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <CommandInput placeholder="Cerca cliente (cognome, nome, email, telefono, CF, città)…" data-testid={`${testid}-input`} />
          <CommandList
            className="max-h-[60vh] overflow-y-auto overflow-x-hidden overscroll-contain"
            data-testid={`${testid}-list`}
          >
            <CommandEmpty>
              <div className="text-sm py-3 text-center text-slate-500">
                Nessun cliente trovato.
                {allowCreate && (
                  <div className="mt-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => { setCreateOpen(true); setOpen(false); }}
                      data-testid={`${testid}-create-empty`}
                      className="text-blue-700 border-blue-300 hover:bg-blue-50"
                    >
                      <UserPlus className="h-3.5 w-3.5 mr-1" /> Crea nuovo cliente
                    </Button>
                  </div>
                )}
              </div>
            </CommandEmpty>
            {allowCreate && (
              <CommandGroup>
                <CommandItem
                  value="__nuovo_cliente_action__"
                  onSelect={() => { setCreateOpen(true); setOpen(false); }}
                  data-testid={`${testid}-create-btn`}
                  className="text-blue-700 font-medium"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Nuovo cliente…
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup>
              {sorted.map((c) => {
                const cleanSurname = (c.surname || "").trim() === "-" ? "" : (c.surname || "");
                const haystack = [
                  c.ragione_sociale || "",
                  c.company_name || "",
                  cleanSurname,
                  c.name || "",
                  c.email || "",
                  c.mobile || "",
                  c.phone || "",
                  c.fiscal_code || "",
                  c.city || "",
                  c.address || "",
                  c.id || "",
                ].join(" ").toLowerCase();
                return (
                  <CommandItem
                    key={c.id}
                    value={haystack}
                    data-testid={`${testid}-item-${c.id}`}
                    onSelect={() => {
                      onChange?.(c.id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-1 h-4 w-4",
                        value === c.id ? "opacity-100 text-emerald-600" : "opacity-0",
                      )}
                    />
                    <div className="flex flex-col min-w-0">
                      <div className="font-medium truncate flex items-center gap-1.5">
                        <span className="truncate">{labelOf(c)}</span>
                        {c.city && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] font-medium uppercase tracking-wide bg-slate-100 text-slate-600 border border-slate-300 rounded px-1.5 py-0.5 shrink-0">
                            📍 {c.city}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {c.fiscal_code ? <span className="font-mono">{c.fiscal_code}</span> : "—"} · {c.email || "—"} · {c.mobile || c.phone || "—"}
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>

    {/* 🆕 Dialog inline per creare un nuovo cliente senza uscire dal wizard */}
    <Dialog open={createOpen} onOpenChange={(o) => { if (!o && !creating) setCreateOpen(false); }}>
      <DialogContent className="max-w-lg" data-testid={`${testid}-create-dialog`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-blue-700">
            <UserPlus className="h-5 w-5" /> Nuovo cliente
          </DialogTitle>
          <DialogDescription>
            Inserisci i dati essenziali. Potrai completare la scheda cliente in un secondo momento dalla sezione Clienti.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <Label className="text-xs">Cognome *</Label>
            <Input
              autoFocus
              value={newClient.surname}
              onChange={(e) => setNewClient((f) => ({ ...f, surname: e.target.value }))}
              data-testid={`${testid}-new-surname`}
            />
          </div>
          <div>
            <Label className="text-xs">Nome *</Label>
            <Input
              value={newClient.name}
              onChange={(e) => setNewClient((f) => ({ ...f, name: e.target.value }))}
              data-testid={`${testid}-new-name`}
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Codice Fiscale</Label>
            <Input
              value={newClient.fiscal_code}
              onChange={(e) => setNewClient((f) => ({ ...f, fiscal_code: e.target.value.toUpperCase() }))}
              className="font-mono uppercase"
              maxLength={16}
              data-testid={`${testid}-new-cf`}
            />
          </div>
          <div>
            <Label className="text-xs">Cellulare</Label>
            <Input
              value={newClient.mobile}
              onChange={(e) => setNewClient((f) => ({ ...f, mobile: e.target.value }))}
              data-testid={`${testid}-new-mobile`}
            />
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input
              type="email"
              value={newClient.email}
              onChange={(e) => setNewClient((f) => ({ ...f, email: e.target.value }))}
              data-testid={`${testid}-new-email`}
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs">Città</Label>
            <Input
              value={newClient.city}
              onChange={(e) => setNewClient((f) => ({ ...f, city: e.target.value }))}
              data-testid={`${testid}-new-city`}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => setCreateOpen(false)} disabled={creating}>
            Annulla
          </Button>
          <Button
            size="sm"
            onClick={submitNewClient}
            disabled={creating}
            data-testid={`${testid}-new-submit`}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {creating && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Crea e seleziona
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
}

export default ClientCombobox;
